/**
 * One lane of the queue, with all four of its states.
 *
 * The shell exists so no lane can accidentally answer a failed read with its
 * empty copy — the thing this product gets wrong most often and can least afford
 * (see `api/readOutcome`). A lane renders exactly one of: its rows, a skeleton,
 * a named failure with a retry, or an empty state that says what to do next. A
 * lane the reader has no permission for renders nothing at all, which is how the
 * nav already treats those surfaces.
 */
import * as React from "react";
import { Link } from "@tanstack/react-router";
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, EmptyState, ErrorState, Skeleton } from "@authe/ui-core";
import type { LaneOutcome } from "./useQueueLanes";

export interface QueueSectionProps {
  title: string;
  /** What this lane is FOR, in one line — shown above the rows, not on hover. */
  description: string;
  outcome: LaneOutcome;
  /** Rows the lane is showing (already trimmed by the hook). */
  children: React.ReactNode;
  /** How many the lane holds beyond what it shows. */
  more?: number;
  /** Where the full surface lives. */
  seeAll: { to: string; label: string };
  retry: () => void;
  /** Why it failed, where the read can say — see {@link QueueLane.error}. */
  error?: unknown;
  /** What an empty lane should say, and the action that follows from it. */
  empty: { title: string; description: string; action?: React.ReactNode };
  /** What a failed lane must not be mistaken for. */
  failedTitle: string;
}

export function QueueSection({
  title,
  description,
  outcome,
  children,
  more = 0,
  seeAll,
  retry,
  error,
  empty,
  failedTitle,
}: QueueSectionProps): React.ReactElement | null {
  // Not "no rows": a lane the caller cannot read is not part of their queue, and
  // an empty state for it would be a claim about data they were never shown.
  if (outcome === "unavailable") return null;

  return (
    <Card className="min-w-0">
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
        <div className="min-w-0">
          <CardTitle className="flex items-center gap-2">
            {title}
            {outcome === "ready" && more > 0 && (
              <Badge variant="secondary" className="font-normal">
                +{more} more
              </Badge>
            )}
          </CardTitle>
          <p className="mt-1 text-xs text-muted-foreground">{description}</p>
        </div>
        <Button asChild size="sm" variant="ghost" className="shrink-0">
          <Link to={seeAll.to}>{seeAll.label}</Link>
        </Button>
      </CardHeader>
      <CardContent>
        {outcome === "loading" ? (
          <div className="space-y-2" aria-hidden>
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-2/3" />
          </div>
        ) : outcome === "failed" ? (
          // Deliberately NOT the empty copy: "nothing fired" and "we could not
          // ask" lead an analyst to opposite conclusions.
          <ErrorState size="sm" title={failedTitle} error={error} onRetry={retry} />
        ) : outcome === "empty" ? (
          <EmptyState
            size="sm"
            title={empty.title}
            description={empty.description}
            action={empty.action}
          />
        ) : (
          <ul className="divide-y divide-border/60">{children}</ul>
        )}
      </CardContent>
    </Card>
  );
}

/** A lane row: one subject, its qualifiers, and the time it happened. */
export function QueueRow({
  to,
  lead,
  meta,
  when,
  marker,
}: {
  to: string;
  /** The subject — what the reader is looking for when they scan. */
  lead: React.ReactNode;
  meta?: React.ReactNode;
  when?: string;
  /** Severity, status, or nothing. */
  marker?: React.ReactNode;
}): React.ReactElement {
  return (
    <li>
      <Link
        to={to}
        className="flex items-center gap-3 py-2 text-sm hover:bg-accent/40 focus-visible:bg-accent/40"
      >
        {marker}
        <span className="min-w-0 flex-1 truncate font-medium">{lead}</span>
        {meta && <span className="shrink-0 text-xs text-muted-foreground">{meta}</span>}
        {when && <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{when}</span>}
      </Link>
    </li>
  );
}
